import {
    ChangeDetectionStrategy,
    Component,
    ViewEncapsulation,
    input,
    viewChild,
    inject,
    computed,
} from "@angular/core";
import { Table } from "../../s25-table/Table";
import { Bind } from "../../../decorators/bind.decorator";
import { GenericTableButtonComponent } from "../../s25-table/generics/generic.table.button.component";
import { DocumentListItem, DocumentService } from "../../../services/document.service";
import { S25TableComponent } from "../../s25-table/s25.table.component";
import { S25Util } from "../../../util/s25-util";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { CacheRepository } from "../../../decorators/cache.decorator";
import { Doc } from "./s25.document.const";
import { FlsService } from "../../../services/fls.service";
import { hasEditFls, hasFullFls } from "../../../pojo/Fls";
import { ReportService } from "../../../services/report.service";
import { S25Help } from "../../s25-help/s25.help.service";
import { GenericTableFadePreviewComponent } from "../../s25-table/generics/generic.table.fade.preview.component";
import { GenericTableRenderHTMLComponent } from "../../s25-table/generics/generic.table.render.html.component";
import { S25ModalComponent } from "../../s25-modal/s25.modal.component";
import { TablePopoverButtonComponent } from "../../s25-table/generics/table.popover.button.component";
import { S25UsageComponent, Usage } from "../../../standalone/s25.usage.component";
import { DocumentUtil } from "./s25.document.util";
import { s25Resource } from "../../../util/s25.resource";
import { UserStore } from "../../../stores/user.store";

@TypeManagerDecorator("s25-ng-document-list")
@Component({
    selector: "s25-ng-document-list",
    template: `
        <div class="top">
            @if (!isTemplate()) {
                <div class="header">
                    <button class="aw-button aw-button--primary" (click)="onCreateClick()" [disabled]="!canCreate()">
                        Create Document
                    </button>
                    @if (canExport()) {
                        <button class="aw-button aw-button--primary" (click)="importModal.open()">
                            Import Document
                        </button>
                    }
                </div>
                @if (!canCreate()) {
                    <div class="small c-margin-bottom--single ">
                        Note: You will need the permissions described
                        <a href="{{ documentHelpLink }}" s25Help [helpTopic]="'document'" target="_blank">here</a>
                        in order to create a document.
                    </div>
                }
            }
            @if (!status.isLoading()) {
                <s25-ng-table
                    #table
                    [dataSource]="tableConfig()"
                    [hasRefresh]="true"
                    [hasFilter]="true"
                    [columnSortable]="true"
                />
            }
        </div>

        <s25-ng-modal #documentModal [size]="'xl'" [resizable]="true" id="dm--edit-document-modal">
            <ng-template #s25ModalBody let-row="row" let-mode="mode" let-isTemplate="isTemplate" let-scope="scope">
                <s25-ng-edit-document
                    [id]="row?.id ?? undefined"
                    [mode]="mode"
                    [isTemplate]="isTemplate"
                    [scope]="scope"
                    (saved)="tableComponent()?.refresh(); documentModal.close()"
                    (copied)="onCopyClick(row)"
                    (cancelled)="documentModal.close()"
                />
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #deleteModal [size]="'sm'">
            <ng-template #s25ModalBody let-name="name" let-usage="usage">
                @if (usage) {
                    <h3>This subreport is used by</h3>
                    <ul>
                        @for (use of usage; track use.document_id) {
                            <li>{{ use.document_name }}</li>
                        }
                    </ul>
                } @else {
                    Are you sure you want to delete this document?
                    <p class="ngBold">{{ name }}</p>
                }
            </ng-template>
        </s25-ng-modal>

        <s25-ng-modal #importModal [size]="'lg'" [title]="'Import Document'">
            <ng-template #s25ModalBody>
                <s25-ng-import-document (imported)="importModal.close(); tableComponent()?.refresh()" />
            </ng-template>
        </s25-ng-modal>
    `,
    styles: `
        .top {
            max-width: 1200px;
        }

        .header {
            display: flex;
            justify-content: space-between;
        }

        .header > button {
            margin-bottom: 1em;
        }

        #dm--edit-document-modal {
            /* Start modal at 1220px to match printed document size */
            --resized-width: 1220px;
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class S25DocumentListComponent {
    readonly isTemplate = input<boolean>(false);
    readonly scope = input<Doc.Scope>();

    protected readonly tableComponent = viewChild(S25TableComponent);
    protected readonly documentModal = viewChild("documentModal", { read: S25ModalComponent });
    protected readonly deleteModal = viewChild("deleteModal", { read: S25ModalComponent });

    private readonly userStore = inject(UserStore);

    private readonly fls = s25Resource(FlsService.getFls);
    private readonly defaultFilenames = s25Resource(ReportService.getDefaultDocumentFileNames);
    private readonly documents = s25Resource({
        params: () => ({ scope: this.scope(), isTemplate: this.isTemplate() }),
        loader: ({ params: { scope, isTemplate }, abortSignal }) => {
            if (isTemplate) {
                return DocumentService.getDocumentTemplateList();
            } else {
                return DocumentService.getDocumentList({ scopes: scope ? [scope] : ["null", "related"] });
            }
        },
    });
    protected readonly status = s25Resource.combine(this.fls, this.defaultFilenames);

    protected readonly creatableTypes = computed((): Set<Doc.TypeId> => {
        const defaultFilenames = this.defaultFilenames.value();
        if (!defaultFilenames) return new Set();

        // All reports in defaultFileNames have at least view perms, which is what is required for creation
        const creatableTypes = new Set(
            Object.keys(defaultFilenames).map((rptId) => Doc.typeByReportId.get(Number(rptId)).id),
        );
        // Also add types without any associated report (and thus no OLS preventing creation)
        for (const type of Doc.allTypes) {
            if (!type.reportId) creatableTypes.add(type.id);
        }
        return creatableTypes;
    });
    protected readonly canCreate = computed(() => {
        const defaultFilenames = this.defaultFilenames.value();
        return hasFullFls(this.fls.value()?.REP_LIST) && defaultFilenames && !!Object.keys(defaultFilenames).length;
    });
    protected readonly canDelete = computed(() => hasFullFls(this.fls.value()?.REP_LIST) && !this.isTemplate());
    protected readonly canExport = computed(() => !this.isTemplate() && this.userStore.isService25());
    protected readonly tableConfig = computed((): Table.DataSource => {
        const columns: Table.Column[] = [
            { id: "name", header: "Name", width: 250 },
            {
                id: "description",
                header: "Description",
                cells: {
                    component: GenericTableFadePreviewComponent,
                },
            },
            this.isTemplate() && { id: "scope", header: "Type" },
            { id: "type", header: "Scope" },
            this.canExport() && GenericTableButtonComponent.Column("Export", this.onExportClick, "outline"),
            this.isTemplate() && GenericTableButtonComponent.Column("Sample", this.onSampleClick, "outline"),
            this.canCreate() && GenericTableButtonComponent.Column("Copy", this.onCopyClick, "outline"),
            GenericTableButtonComponent.Column("View", this.onEditClick, "outline"),
            this.scope() === "subreport" && {
                id: "usage",
                header: "Usage",
                width: "min-content" as const,
                sortable: false,
                cells: { component: TablePopoverButtonComponent },
            },
            this.canDelete() && GenericTableButtonComponent.Column("Delete", this.onDeleteClick, "danger--outline"),
        ].filter((col) => col);

        return { type: "unpaginated", dataSource: this.getRows, columns };
    });
    readonly documentsById = computed((): Map<number, DocumentListItem> => {
        const documents = this.documents.value();
        if (!documents) return new Map();
        return new Map(documents.map((doc) => [doc.itemId, doc]));
    });

    protected readonly documentHelpLink = S25Help.getHelpUrl("document");

    @Bind
    async getRows(query: Table.UnpaginatedQuery): Promise<Table.DataSourceResponse> {
        if (query.forceRefresh) this.invalidateCache();

        const documents = (await this.documents.reloadAndAwait()) ?? [];

        return {
            rows: documents.map(this.mapToRow),
        };
    }

    invalidateCache() {
        if (this.isTemplate()) {
            CacheRepository.invalidateByService("DocumentService", "getDocumentTemplateList");
        } else {
            CacheRepository.invalidateByService("DocumentService", "getDocumentList");
        }
    }

    @Bind
    mapToRow(doc: DocumentListItem): Table.Row {
        let copyDisabledReason: string;
        if (!this.isTemplate() && !hasFullFls(doc.accessLevel)) {
            copyDisabledReason = `You lack permission to copy this document`;
        } else if (!this.creatableTypes().has(doc.type)) {
            copyDisabledReason = `You lack permission to create documents of this scope`;
        }
        return {
            id: doc.itemId,
            name: doc.itemName,
            cells: {
                name: { text: doc.itemName },
                type: { text: Doc.type[doc.type]?.label || doc.type },
                scope: { text: doc.scope === "subreport" ? "Subreport" : "" },
                description: {
                    textValue: doc.description,
                    inputs: {
                        cell: {
                            component: GenericTableRenderHTMLComponent,
                            inputs: { text: doc.description ?? "", isHtml: true },
                        },
                    },
                },
                sample: {
                    if: !!doc.sampleFilename,
                    inputs: {
                        label: "Sample",
                        type: "outline",
                    },
                },
                copy: {
                    inputs: {
                        label: "Copy",
                        type: "outline",
                        disabled: !!copyDisabledReason,
                        disabledReason: copyDisabledReason,
                    },
                },
                view: {
                    inputs: {
                        label: hasEditFls(doc.accessLevel) ? "Edit" : "View",
                        type: "outline",
                    },
                },
                usage: {
                    inputs: {
                        label: "Usage",
                        cell: {
                            component: S25UsageComponent,
                            inputs: { service: this.getUsage.bind(this, doc) },
                        },
                    },
                },
                delete: {
                    inputs: {
                        label: "Delete",
                        type: "danger--outline",
                        disabled: !hasFullFls(doc.accessLevel),
                        disabledReason: "You lack permission to delete this document",
                    },
                },
            },
        };
    }

    async onCreateClick() {
        await this.documentModal()?.open({
            title: `Create Document`,
            templateData: { mode: "create", scope: this.scope() },
        });
    }

    @Bind
    async onEditClick(row: Table.Row) {
        const document = this.documentsById().get(row.id as number);
        const mode = hasEditFls(document.accessLevel) ? "edit" : "view";

        // Templates have both normal documents and subreports, so we need to get it from the type
        const scope = Doc.type[document.type].interface;
        await this.documentModal()?.open({
            title: `${S25Util.firstCharToUpper(mode)} Document: ${row.name}`,
            templateData: { row, mode, isTemplate: this.isTemplate(), scope },
        });
    }

    @Bind
    async onCopyClick(row: Table.Row) {
        await this.documentModal()?.close();
        const document = this.documentsById().get(row.id as number);
        // Templates have both normal documents and subreports, so we need to get it from the type
        const scope = Doc.type[document.type].interface;
        await this.documentModal()?.open({
            title: `Copy Document: ${row.name}`,
            templateData: { row, mode: "copy", isTemplate: this.isTemplate(), scope },
        });
    }

    @Bind
    async onDeleteClick(row: Table.Row) {
        // Check if the document is in use
        const [usage, usageErr] = await DocumentService.getUsage(row.id as number);
        if (usageErr) {
            alert(`Something went wrong. Please try again`);
            return;
        }

        if (!!usage.length) {
            await this.deleteModal()?.open({
                type: "acknowledge",
                title: "Unable To Delete",
                templateData: { name: row.name, usage },
            });
            return;
        }

        // Document is not in use
        const ok = await this.deleteModal()?.open({
            type: "deny",
            title: "Confirm Deletion",
            templateData: { name: row.name },
        });
        if (!ok) return; // User answered no

        const [_, error] = await S25Util.Maybe(DocumentService.deleteDocument(row.id as number));
        if (error) return S25Util.showError(error);
        return this.tableComponent()?.refresh();
    }

    @Bind
    async onSampleClick(row: Table.Row) {
        const doc = this.documentsById().get(row.id as number);
        DocumentService.downloadSample(doc.itemId, doc.sampleFilename);
    }

    @Bind
    async getUsage(doc: DocumentListItem): Promise<Usage> {
        const [usage, err] = await DocumentService.getUsage(doc.itemId);

        if (err) return { error: `Could not fetch usage. Please try again` };

        return {
            name: doc.itemName,
            types: [
                {
                    type: "Document Usage",
                    uses: usage.map((use) => ({ name: use.document_name })),
                    noUsesMessage: "No documents use this subreport",
                },
            ],
        };
    }

    async onExportClick(row: Table.Row) {
        await DocumentUtil.exportDocument(row.id as number, row.name);
    }
}
